import React, { useMemo, useState } from "react";
import SalesTable from "../../InventoryManagement/SalesInvoice/SalesTable";
import Swal from "sweetalert2";
import useApi from "../../../../../utils/api-manager/Helper/useApi";
import { toast, ToastContainer } from "react-toastify";
import useFormattedDate from "../../../../../context/useFormattingUtils";
import useFormattingUtils from "../../../../../context/useFormattingUtils";
import QuickInvoicemodal from "../../Dashboard/Invoice/QuickInvoicemodal";
import InvoiceCsvExport from "./InvoiceCsvExport";

function RetailInvoiceHistory({
  endpoint = "getRetailInvoice",
  pageTitle = "Retail Invoice History",
  badgeLabel = "Retail Sales Collection",
  description = "Track invoices created for products sold by your store and monitor the money received from retail customers.",
  invoiceSubtitle = "Retail sale receipt",
  deleteTitle = "Delete retail sale invoice?",
  deleteSuccessMessage = "Retail sale invoice deleted successfully.",
  fetchErrorLabel = "retail invoice",
  previewErrorLabel = "retail invoice",
  collectionSubtitle = "Retail collection amount",
  exportTitle = "Retail CSV Export",
  filenamePrefix = "retail-invoice-report",
  invoiceType = "retail",
}) {
  const { Delete, Post, GetPaginatedData } = useApi();

  const [showInvoice, setShowInvoice] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [salesInvoiceData, setSalesInvoiceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [tableRefreshKey, setTableRefreshKey] = useState(0);
  const [showExportModal, setShowExportModal] = useState(false);

  const { formatDate } = useFormattedDate();
  const { formatCurrency } = useFormattingUtils();

  const safeCurrency = (value) => {
    const amount = parseFloat(value || 0);

    if (Number.isNaN(amount)) {
      return formatCurrency ? formatCurrency(0) : "0.00";
    }

    return formatCurrency ? formatCurrency(amount) : amount.toFixed(2);
  };

  const safeDate = (value) => {
    return value ? formatDate(value) : "N/A";
  };

  const getPaymentMethod = (value) => {
    const method = String(value || "N/A").trim();
    return method || "N/A";
  };

  const fetchSalesInvoiceData = async ({
    page = 1,
    searchTerm = "",
    sort = "",
  } = {}) => {
    const trimmedSearchTerm = String(searchTerm || searchQuery || "").trim();

    try {
      setLoading(true);

      const response = await GetPaginatedData(endpoint, {
        page,
        search: trimmedSearchTerm || undefined,
        queryParams: {
          ...(sort && { sort }),
        },
      });

      const results = Array.isArray(response?.data) ? response.data : [];
      const totalPages = Math.ceil((response?.totalCount || 0) / 10) || 1;

      const formatted = results.map((item) => ({
        ...item,
        date: item.date ? formatDate(item.date) : "N/A",
        raw_date: item.date,
      }));

      setSalesInvoiceData(formatted);

      return { data: formatted, totalPages };
    } catch (error) {
      console.error(`Error fetching ${fetchErrorLabel} data:`, error);
      return { data: [], totalPages: 1 };
    } finally {
      setLoading(false);
    }
  };

  const fetchRetailDataForTable = async ({ page = 1, searchTerm = "" }) => {
    return await fetchSalesInvoiceData({
      page,
      searchTerm: searchTerm || searchQuery,
    });
  };

  const handleSearchSubmit = (e) => {
    if (e) {
      e.preventDefault();
    }

    setTableRefreshKey((prev) => prev + 1);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setTableRefreshKey((prev) => prev + 1);
  };

  const handleRefresh = () => {
    setTableRefreshKey((prev) => prev + 1);
  };

  const fetchAllRetailInvoicesForExport = async () => {
    const pageSize = 100;
    const trimmedSearchTerm = String(searchQuery || "").trim();

    const firstResponse = await GetPaginatedData(endpoint, {
      page: 1,
      search: trimmedSearchTerm || undefined,
      queryParams: { page_size: pageSize },
    });

    const firstRows = Array.isArray(firstResponse?.data) ? firstResponse.data : [];
    const totalPages = Math.ceil((firstResponse?.totalCount || firstRows.length) / pageSize) || 1;
    const rows = [...firstRows];

    for (let page = 2; page <= totalPages; page += 1) {
      const response = await GetPaginatedData(endpoint, {
        page,
        search: trimmedSearchTerm || undefined,
        queryParams: { page_size: pageSize },
      });

      rows.push(...(Array.isArray(response?.data) ? response.data : []));
    }

    return rows.map((item) => ({
      ...item,
      raw_date: item.raw_date || item.date,
      date: item.date ? formatDate(item.date) : "N/A",
    }));
  };

  const handlePrint = async (invoiceId, openModal = false) => {
    try {
      setLoading(true);

      const response = await Post("getSalesInvoiceDetails", {
        invoice_id: invoiceId,
      });

      if (response?.data && openModal) {
        setModalData(response.data);
        setShowInvoice(true);
      }
    } catch (error) {
      console.error("Error fetching invoice details:", error);
      toast.error(`Failed to open ${previewErrorLabel} preview.`);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowInvoice(false);
    setModalData(null);
  };

  const handleDeleteProduct = async (invoiceId) => {
    const result = await Swal.fire({
      title: deleteTitle,
      text: "This will remove the invoice history record. This action cannot be reverted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#0f766e",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      await Delete("salesInvoiceData", invoiceId);

      setSalesInvoiceData((prevData) =>
        prevData.filter((invoice) => invoice.id !== invoiceId)
      );

      toast.success(deleteSuccessMessage);
      setTableRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error("Error deleting retail invoice:", error);
      toast.error("Failed to delete retail invoice.");
    }
  };

  const stats = useMemo(() => {
    const invoiceCount = salesInvoiceData.length;

    const totalReceived = salesInvoiceData.reduce(
      (total, invoice) => total + (parseFloat(invoice.payable_amount) || 0),
      0
    );

    const cashReceived = salesInvoiceData
      .filter((invoice) => getPaymentMethod(invoice.pay_method).toLowerCase() === "cash")
      .reduce((total, invoice) => total + (parseFloat(invoice.payable_amount) || 0), 0);

    const digitalReceived = salesInvoiceData
      .filter((invoice) => getPaymentMethod(invoice.pay_method).toLowerCase() !== "cash")
      .reduce((total, invoice) => total + (parseFloat(invoice.payable_amount) || 0), 0);

    const averageSale =
      invoiceCount > 0 ? totalReceived / invoiceCount : 0;

    return {
      invoiceCount,
      totalReceived,
      cashReceived,
      digitalReceived,
      averageSale,
    };
  }, [salesInvoiceData]);

  const columns = [
    {
      field: "invoice_no",
      header: "Invoice No",
      body: (rowData) => (
        <div className="rih-invoice-cell">
          <div className="rih-invoice-no">{rowData.invoice_no || "-"}</div>
          <div className="rih-invoice-subtitle">{invoiceSubtitle}</div>
        </div>
      ),
    },
    {
      field: "payable_amount",
      header: "Money Received",
      body: (rowData) => (
        <span className="rih-money-pill">
          {safeCurrency(rowData.payable_amount)}
        </span>
      ),
    },
    {
      field: "pay_method",
      header: "Collection Mode",
      body: (rowData) => {
        const paymentMethod = getPaymentMethod(rowData.pay_method);
        const isCash = paymentMethod.toLowerCase() === "cash";

        return (
          <span className={isCash ? "rih-pay-pill rih-pay-cash" : "rih-pay-pill rih-pay-other"}>
            {paymentMethod}
          </span>
        );
      },
    },
    {
      field: "date",
      header: "Sale Date",
      body: (rowData) => (
        <div className="rih-date-cell">
          {rowData.raw_date ? safeDate(rowData.raw_date) : rowData.date || "N/A"}
        </div>
      ),
    },
  ];

  const exportColumns = [
    { key: "invoice_no", label: "Invoice No", getValue: (invoice) => invoice.invoice_no || "" },
    { key: "date", label: "Sale Date", getValue: (invoice) => safeDate(invoice.raw_date || invoice.date) },
    { key: "payable_amount", label: "Money Received", getValue: (invoice) => parseFloat(invoice.payable_amount || 0).toFixed(2) },
    { key: "pay_method", label: "Collection Mode", getValue: (invoice) => getPaymentMethod(invoice.pay_method) },
    { key: "customer", label: "Customer", getValue: (invoice) => invoice.customer || invoice.customer_name || "Walk-in / Unassigned" },
    { key: "created_at", label: "Created At", getValue: (invoice) => invoice.created_at || "" },
  ];

  const renderStatCard = ({
    title,
    value,
    subtitle,
    icon,
    tone = "default",
  }) => (
    <div className={`rih-stat-card rih-stat-${tone}`}>
      <div className="rih-stat-topline">
        <span className="rih-stat-icon">{icon}</span>
        <span>{title}</span>
      </div>
      <div className="rih-stat-value">{value}</div>
      {subtitle && <div className="rih-stat-subtitle">{subtitle}</div>}
    </div>
  );

  return (
    <div>
      <ToastContainer />

      <style>
        {`
          .rih-page {
            background:
              radial-gradient(circle at top left, rgba(20, 184, 166, 0.12), transparent 34%),
              linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
            min-height: 100vh;
            padding: 6px 0 24px;
          }

          .rih-shell {
            width: 100%;
          }

          .rih-hero {
            background:
              linear-gradient(135deg, #0f766e 0%, #115e59 58%, #134e4a 100%);
            border-radius: 26px;
            padding: 26px;
            color: #ffffff;
            margin-bottom: 18px;
            box-shadow: 0 22px 50px rgba(15, 118, 110, 0.2);
            position: relative;
            overflow: hidden;
          }

          .rih-hero::after {
            content: "";
            position: absolute;
            width: 240px;
            height: 240px;
            right: -80px;
            top: -90px;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.13);
          }

          .rih-hero-content {
            position: relative;
            z-index: 1;
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 22px;
            flex-wrap: wrap;
          }

          .rih-badge {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            background: rgba(255, 255, 255, 0.16);
            border: 1px solid rgba(255, 255, 255, 0.28);
            color: #ccfbf1;
            padding: 8px 13px;
            border-radius: 999px;
            font-weight: 900;
            font-size: 12px;
            margin-bottom: 13px;
          }

          .rih-title {
            margin: 0;
            font-size: 31px;
            font-weight: 950;
            letter-spacing: -0.04em;
            line-height: 1.08;
          }

          .rih-description {
            margin: 9px 0 0;
            color: rgba(255, 255, 255, 0.82);
            font-size: 14px;
            max-width: 620px;
            line-height: 1.55;
          }

          .rih-hero-actions {
            display: flex;
            gap: 10px;
            flex-wrap: wrap;
            justify-content: flex-end;
          }

          .rih-primary-btn,
          .rih-secondary-btn,
          .rih-light-btn {
            border: none;
            border-radius: 999px;
            padding: 10px 16px;
            font-size: 13px;
            font-weight: 900;
            cursor: pointer;
            transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
            white-space: nowrap;
          }

          .rih-primary-btn:hover,
          .rih-secondary-btn:hover,
          .rih-light-btn:hover {
            transform: translateY(-1px);
          }

          .rih-light-btn {
            background: #ffffff;
            color: #0f766e;
            box-shadow: 0 10px 24px rgba(15, 23, 42, 0.13);
          }

          .rih-secondary-btn {
            background: rgba(255, 255, 255, 0.12);
            color: #ffffff;
            border: 1px solid rgba(255, 255, 255, 0.28);
          }

          .rih-primary-btn {
            background: #0f766e;
            color: #ffffff;
          }

          .rih-btn-disabled {
            opacity: 0.62;
            cursor: not-allowed;
          }

          .rih-stats-grid {
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 14px;
            margin-bottom: 18px;
          }

          .rih-stat-card {
            background: rgba(255, 255, 255, 0.92);
            border: 1px solid #e5e7eb;
            border-radius: 20px;
            padding: 17px 18px;
            box-shadow: 0 12px 32px rgba(15, 23, 42, 0.06);
            backdrop-filter: blur(8px);
          }

          .rih-stat-revenue {
            background: linear-gradient(180deg, #ecfdf5 0%, #ffffff 100%);
            border-color: #bbf7d0;
          }

          .rih-stat-cash {
            background: linear-gradient(180deg, #f0fdfa 0%, #ffffff 100%);
            border-color: #99f6e4;
          }

          .rih-stat-digital {
            background: linear-gradient(180deg, #eff6ff 0%, #ffffff 100%);
            border-color: #bfdbfe;
          }

          .rih-stat-average {
            background: linear-gradient(180deg, #fff7ed 0%, #ffffff 100%);
            border-color: #fed7aa;
          }

          .rih-stat-topline {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 11px;
            color: #64748b;
            font-weight: 950;
            text-transform: uppercase;
            letter-spacing: 0.055em;
          }

          .rih-stat-icon {
            width: 28px;
            height: 28px;
            border-radius: 10px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            background: #f1f5f9;
            font-size: 15px;
          }

          .rih-stat-value {
            margin-top: 10px;
            font-size: 25px;
            font-weight: 950;
            color: #0f172a;
            line-height: 1;
            word-break: break-word;
          }

          .rih-stat-subtitle {
            margin-top: 8px;
            font-size: 12px;
            color: #64748b;
            font-weight: 750;
          }

          .rih-table-card {
            background: #ffffff;
            border: 1px solid #e5e7eb;
            border-radius: 24px;
            padding: 18px;
            box-shadow: 0 16px 42px rgba(15, 23, 42, 0.07);
          }

          .rih-table-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 16px;
            flex-wrap: wrap;
            margin-bottom: 18px;
          }

          .rih-section-title {
            margin: 0;
            font-size: 21px;
            font-weight: 950;
            color: #0f172a;
            letter-spacing: -0.02em;
          }

          .rih-section-subtitle {
            margin: 5px 0 0;
            color: #64748b;
            font-size: 12px;
            font-weight: 750;
          }

          .rih-search-form {
            display: flex;
            align-items: center;
            gap: 10px;
            flex-wrap: wrap;
          }

          .rih-search-wrap {
            position: relative;
          }

          .rih-search-icon {
            position: absolute;
            left: 14px;
            top: 50%;
            transform: translateY(-50%);
            color: #94a3b8;
            font-size: 14px;
          }

          .rih-search-input {
            width: 285px;
            height: 42px;
            border: 1px solid #d1d5db;
            border-radius: 999px;
            padding: 0 16px 0 38px;
            font-size: 13px;
            outline: none;
            font-weight: 750;
            color: #111827;
            background: #ffffff;
            transition: border 0.2s ease, box-shadow 0.2s ease;
          }

          .rih-search-input:focus {
            border-color: #14b8a6;
            box-shadow: 0 0 0 4px rgba(20, 184, 166, 0.12);
          }

          .rih-table-shell {
            border: 1px solid #e5e7eb;
            border-radius: 18px;
            overflow: hidden;
            background: #ffffff;
          }

          .rih-invoice-cell {
            display: flex;
            flex-direction: column;
            gap: 3px;
          }

          .rih-invoice-no {
            font-weight: 950;
            color: #0f172a;
          }

          .rih-invoice-subtitle {
            font-size: 11px;
            color: #64748b;
            font-weight: 700;
          }

          .rih-money-pill {
            display: inline-flex;
            background: #ecfdf5;
            color: #047857;
            border: 1px solid #a7f3d0;
            border-radius: 999px;
            padding: 7px 11px;
            font-weight: 950;
            font-size: 12px;
            white-space: nowrap;
          }

          .rih-pay-pill {
            display: inline-flex;
            border-radius: 999px;
            padding: 7px 11px;
            font-weight: 950;
            font-size: 12px;
            text-transform: capitalize;
            white-space: nowrap;
          }

          .rih-pay-cash {
            background: #f0fdfa;
            color: #0f766e;
            border: 1px solid #99f6e4;
          }

          .rih-pay-other {
            background: #eff6ff;
            color: #1d4ed8;
            border: 1px solid #bfdbfe;
          }

          .rih-date-cell {
            font-weight: 850;
            color: #0f172a;
            white-space: nowrap;
          }

          @media (max-width: 1200px) {
            .rih-stats-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr));
            }
          }

          @media (max-width: 768px) {
            .rih-page {
              padding: 0 0 18px;
            }

            .rih-hero {
              border-radius: 18px;
              padding: 20px;
            }

            .rih-title {
              font-size: 25px;
            }

            .rih-hero-actions,
            .rih-search-form {
              width: 100%;
              justify-content: stretch;
            }

            .rih-light-btn,
            .rih-secondary-btn,
            .rih-primary-btn {
              flex: 1;
            }

            .rih-stats-grid {
              grid-template-columns: 1fr;
            }

            .rih-table-card {
              padding: 14px;
              border-radius: 18px;
            }

            .rih-table-header {
              align-items: stretch;
            }

            .rih-search-wrap,
            .rih-search-input {
              width: 100%;
            }

            .rih-primary-btn,
            .rih-secondary-btn {
              width: 100%;
            }
          }

          @media (max-width: 480px) {
            .rih-hero-content {
              gap: 16px;
            }

            .rih-stat-value {
              font-size: 22px;
            }

            .rih-table-shell {
              overflow-x: auto;
            }
          }
        `}
      </style>

      <div className="responsive-box rih-page">
        <div className="rih-shell">
          <div className="rih-hero">
            <div className="rih-hero-content">
              <div>
                <div className="rih-badge">
                  <span>●</span>
                  <span>{badgeLabel}</span>
                </div>

                <h2 className="rih-title">{pageTitle}</h2>

                <p className="rih-description">
                  {description}
                </p>
              </div>

              <div className="rih-hero-actions">
                <button
                  type="button"
                  onClick={handleRefresh}
                  disabled={loading}
                  className={`rih-light-btn ${loading ? "rih-btn-disabled" : ""}`}
                >
                  {loading ? "Refreshing..." : "Refresh Data"}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery("");
                    setTableRefreshKey((prev) => prev + 1);
                  }}
                  className="rih-secondary-btn"
                >
                  Reset View
                </button>
              </div>
            </div>
          </div>

          <div className="rih-stats-grid">
            {renderStatCard({
              title: "Sales Invoices",
              value: stats.invoiceCount,
              subtitle: "Loaded in current table",
              icon: "🧾",
            })}

            {renderStatCard({
              title: "Money Received",
              value: safeCurrency(stats.totalReceived),
              subtitle: collectionSubtitle,
              icon: "₹",
              tone: "revenue",
            })}

            {renderStatCard({
              title: "Cash Received",
              value: safeCurrency(stats.cashReceived),
              subtitle: "Collected through cash",
              icon: "💵",
              tone: "cash",
            })}

            {renderStatCard({
              title: "Average Sale",
              value: safeCurrency(stats.averageSale),
              subtitle: "Per invoice average",
              icon: "📈",
              tone: "average",
            })}
          </div>

          <div className="rih-table-card">
            <div className="rih-table-header">
              <div>
                <h3 className="rih-section-title">Sales Receipt List</h3>
                <p className="rih-section-subtitle">
                  Search and manage invoices for product sales where the store received payment.
                </p>
              </div>

              <form onSubmit={handleSearchSubmit} className="rih-search-form">
                <div className="rih-search-wrap">
                  <span className="rih-search-icon">⌕</span>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search invoice, customer, payment..."
                    className="rih-search-input"
                  />
                </div>

                <button type="submit" className="rih-primary-btn">
                  Search
                </button>

                <button
                  type="button"
                  onClick={() => setShowExportModal(true)}
                  className="rih-primary-btn"
                  style={{ background: "#0891b2" }}
                >
                  Export CSV
                </button>

                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery("");
                      setTableRefreshKey((prev) => prev + 1);
                    }}
                    className="rih-secondary-btn"
                    style={{
                      color: "#0f766e",
                      borderColor: "#99f6e4",
                      background: "#f0fdfa",
                    }}
                  >
                    Clear
                  </button>
                )}
              </form>
            </div>

            <div className="rih-table-shell">
              <SalesTable
                key={`${tableRefreshKey}-${searchQuery}`}
                fetchData={fetchRetailDataForTable}
                columns={columns}
                showAction={true}
                onDelete={handleDeleteProduct}
                handlePrint={(invoiceId) => handlePrint(invoiceId, true)}
                enablePagination={true}
                rowsPerPage={10}
                showSearch={false}
              />
            </div>
          </div>

          <InvoiceCsvExport
            isOpen={showExportModal}
            onClose={() => setShowExportModal(false)}
            title={exportTitle}
            columns={exportColumns}
            fetchRows={fetchAllRetailInvoicesForExport}
            getRowDate={(invoice) => invoice.raw_date || invoice.date}
            filenamePrefix={filenamePrefix}
            accent="#0f766e"
          />

          {showInvoice && (
            <QuickInvoicemodal
              open={showInvoice}
              onClose={handleCloseModal}
              data={modalData}
              onPrint={handlePrint}
              invoiceType={invoiceType}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default RetailInvoiceHistory;
